import { ComponentType, MouseEvent, useEffect } from 'react';
import { IoClose } from 'react-icons/io5';

interface LegalModalProps {
  Content?: ComponentType;
  isOpen: boolean;
  onClose: () => void;
  title: string;
}

export default function LegalModal({ Content, isOpen, onClose, title }: LegalModalProps) {
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center animate-fade-in"
      onClick={handleBackdropClick}
    >
      <div className="flex h-[100dvh] w-full max-w-[30rem] flex-col bg-zinc-800 px-1">
        <header className="relative flex h-[3.125rem] shrink-0 items-center justify-center bg-black px-4">
          <h2 className="text-base sm:text-[1.075rem] font-extrabold text-[#ffd400]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center text-white"
            aria-label={`Close ${title}`}
          >
            <IoClose className="text-[1.875rem]" />
          </button>
        </header>

        <div className="min-h-0 flex-1 bg-white rounded-tl-xl">
          <div
            className="h-full overflow-y-auto rounded-t-[0.625rem] bg-white px-[1.875rem] py-5 text-left"
            role="dialog"
            aria-modal="true"
            aria-label={title}
          >
            {Content ? <Content /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
